import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getAvatarSrc } from '../../lib/avatarUtils';
import { ToastType } from '../../types/types';

interface ProfileChipProps {
  onOpenProfile: () => void;
  showToast?: (message: string, type?: ToastType, duration?: number, dismissible?: boolean) => void;
}

export default function ProfileChip({ onOpenProfile, showToast }: ProfileChipProps) {
  const { currentUser, backendUser, signInWithGoogle } = useAuth();
  const [loading, setLoading] = useState(false);

  if (!currentUser) {
    const handleGoogle = async () => {
      setLoading(true);
      try {
        await signInWithGoogle();
      } catch {
        showToast?.('Google sign-in failed. Please try again.', 'error');
      } finally {
        setLoading(false);
      }
    };

    return (
      <button
        onClick={handleGoogle}
        disabled={loading}
        aria-busy={loading}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          width: '100%',
          padding: '10px 12px',
          fontSize: '14px',
          fontWeight: 500,
          lineHeight: '20px',
          whiteSpace: 'nowrap',
          color: '#1F1F1F',
          backgroundColor: '#F2F2F2',
          border: 'none',
          borderRadius: '20px',
          cursor: loading ? 'not-allowed' : 'pointer',
          opacity: loading ? 0.7 : 1,
          transition: 'background-color 0.15s ease',
        }}
        onMouseEnter={e => {
          if (!loading) (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#E8E8E8';
        }}
        onMouseLeave={e => {
          (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#F2F2F2';
        }}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          style={{ flexShrink: 0 }}
        >
          <path
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            fill="#4285F4"
          />
          <path
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            fill="#34A853"
          />
          <path
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
            fill="#FBBC05"
          />
          <path
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            fill="#EA4335"
          />
        </svg>
        Sign in with Google
      </button>
    );
  }

  const displayName = backendUser?.display_name || currentUser.email?.split('@')[0] || '';
  const avatarFilename = backendUser?.user_preferences?.avatar ?? '';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpenProfile}
      onKeyDown={e => e.key === 'Enter' && onOpenProfile()}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 24px',
        background: 'var(--color-surface-subtle)',
        borderRadius: 'var(--radius-md)',
        cursor: 'pointer',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <img
        src={getAvatarSrc(avatarFilename)}
        alt="profile"
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: 'var(--color-avatar-bg, #afdcfd)',
          objectFit: 'cover',
          flexShrink: 0,
        }}
      />
      <span style={{ flex: 1, fontSize: 15, fontWeight: 500, color: 'var(--color-content)' }}>
        {displayName}
      </span>
      <ChevronRight size={16} color="var(--color-content-muted)" />
    </div>
  );
}
